import React, { useState, useMemo, useRef, useEffect } from "react";
import { ChevronDown, ChevronUp, ArrowUpRight, ArrowDownRight, Minus, BarChart2 } from "lucide-react";
import ETFTradeCard from "./ETFTradeCard";

const Sparkline = ({ data, color }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !data || data.length < 2) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const w = canvas.width;
    const h = canvas.height;
    
    ctx.clearRect(0, 0, w, h);
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;

    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.lineJoin = "round";

    data.forEach((v, i) => {
      const x = (i / (data.length - 1)) * w;
      const y = h - ((v - min) / range) * (h - 4) - 2;
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.stroke();
  }, [data, color]);

  return <canvas ref={canvasRef} width={80} height={24} className="opacity-80" />;
};

const ETFTable = ({ data, currentSet, selectedTicker }) => {
  const [sortConfig, setSortConfig] = useState({ key: "return_1y", direction: "desc" });
  const [expandedTicker, setExpandedTicker] = useState(null);

  useEffect(() => {
    if (selectedTicker) {
      const element = document.getElementById(`etf-row-${selectedTicker}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setExpandedTicker(selectedTicker);
      }
    }
  }, [selectedTicker]);

  const sortedData = useMemo(() => {
    let sortableData = [...data];
    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        const aVal = a[sortConfig.key] || 0;
        const bVal = b[sortConfig.key] || 0;
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortableData;
  }, [data, sortConfig]);

  const requestSort = (key) => {
    let direction = "desc";
    if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = "asc";
    }
    setSortConfig({ key, direction });
  };

  const fmt = (v, dec = 2) => v != null ? v.toFixed(dec) : '—';
  const fmtPct = (v, dec = 1) => v != null ? `${v >= 0 ? '+' : ''}${v.toFixed(dec)}%` : '—';
  const valColor = (v) => (v || 0) >= 0 ? "text-emerald-500" : "text-rose-500";

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return <Minus className="h-3 w-3 opacity-20" />;
    return sortConfig.direction === "desc" ? <ChevronDown className="h-3 w-3 text-emerald-500" /> : <ChevronUp className="h-3 w-3 text-emerald-500" />;
  };

  return (
    <div className="w-full overflow-x-auto h-[600px] scrollbar-thin scrollbar-thumb-zinc-800">
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="bg-zinc-900/50 border-b border-zinc-800 sticky top-0 z-10">
            <th className="p-3 text-[12px] font-black uppercase text-zinc-500 tracking-widest text-center w-12">#</th>
            <th className="p-3 text-[12px] font-black uppercase text-zinc-500 tracking-widest cursor-pointer" onClick={() => requestSort("ticker")}>
              <div className="flex items-center gap-1">Ticker <SortIcon column="ticker" /></div>
            </th>
            <th className="p-3 text-[12px] font-black uppercase text-zinc-500 tracking-widest cursor-pointer" onClick={() => requestSort("country")}>
              <div className="flex items-center gap-1">{currentSet === "intl" ? "Country" : "Name"} <SortIcon column="country" /></div>
            </th>
            <th className="p-3 text-[12px] font-black uppercase text-zinc-500 tracking-widest text-right cursor-pointer" onClick={() => requestSort("price")}>
              <div className="flex items-center justify-end gap-1">Price <SortIcon column="price" /></div>
            </th>
            <th className="p-3 text-[12px] font-black uppercase text-zinc-500 tracking-widest text-right cursor-pointer" onClick={() => requestSort("return_3m")}>
              <div className="flex items-center justify-end gap-1">Day% <SortIcon column="return_3m" /></div>
            </th>
            <th className="p-3 text-[12px] font-black uppercase text-zinc-500 tracking-widest text-right cursor-pointer" onClick={() => requestSort("return_1m")}>
              <div className="flex items-center justify-end gap-1">Week% <SortIcon column="return_1m" /></div>
            </th>
            <th className="p-3 text-[12px] font-black uppercase text-zinc-500 tracking-widest text-right cursor-pointer" onClick={() => requestSort("return_6m")}>
              <div className="flex items-center justify-end gap-1">Month% <SortIcon column="return_6m" /></div>
            </th>
            <th className="p-3 text-[12px] font-black uppercase text-zinc-500 tracking-widest text-right cursor-pointer" onClick={() => requestSort("return_1y")}>
              <div className="flex items-center justify-end gap-1">1Y% <SortIcon column="return_1y" /></div>
            </th>
            <th className="p-3 text-[12px] font-black uppercase text-zinc-500 tracking-widest text-right cursor-pointer" onClick={() => requestSort("rs_change")}>
              <div className="flex items-center justify-end gap-1">RS vs SPY <SortIcon column="rs_change" /></div>
            </th>
            <th className="p-3 text-[12px] font-black uppercase text-zinc-500 tracking-widest text-center">Trend (3M)</th>
            <th className="p-3 text-[12px] font-black uppercase text-zinc-500 tracking-widest text-center">Momentum</th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((etf, i) => (
            <React.Fragment key={etf.ticker}>
              <tr 
                id={`etf-row-${etf.ticker}`}
                className={`border-b border-zinc-800/50 cursor-pointer transition-all duration-300 group ${
                  expandedTicker === etf.ticker 
                    ? "bg-emerald-500/10 border-emerald-500/20" 
                    : "hover:bg-zinc-900/40"
                }`}
                onClick={() => setExpandedTicker(expandedTicker === etf.ticker ? null : etf.ticker)}
              >
                <td className="p-3 text-[13px] font-bold text-zinc-600 text-center">{i + 1}</td>
                <td className="p-3">
                   <div className="flex items-center gap-3">
                     <div className="bg-zinc-800 p-1.5 rounded-lg border border-zinc-700 group-hover:border-emerald-500/30 transition-colors">
                        <span className="text-sm font-black text-white">{etf.ticker}</span>
                     </div>
                   </div>
                </td>
                <td className="p-3 text-sm font-bold text-zinc-400">{etf.country}</td>
                <td className="p-3 text-right text-sm font-black text-white">${fmt(etf.price)}</td>
                <td className={`p-3 text-right text-sm font-black ${valColor(etf.return_3m)}`}>{fmtPct(etf.return_3m)}</td>
                <td className={`p-3 text-right text-sm font-black ${valColor(etf.return_1m)}`}>{fmtPct(etf.return_1m)}</td>
                <td className={`p-3 text-right text-sm font-black ${valColor(etf.return_6m)}`}>{fmtPct(etf.return_6m)}</td>
                <td className={`p-3 text-right text-base font-black ${valColor(etf.return_1y)}`}>{fmtPct(etf.return_1y)}</td>
                <td className="p-3 text-right">
                   <div className={`flex items-center justify-end gap-1 text-sm font-black ${valColor(etf.rs_change)}`}>
                     {etf.rs_trend === 'up' ? <ArrowUpRight className="h-3 w-3" /> : (etf.rs_trend === 'down' ? <ArrowDownRight className="h-3 w-3" /> : <Minus className="h-3 w-3" />)}
                     {fmtPct(etf.rs_change)}
                   </div>
                </td>
                <td className="p-3">
                   <div className="flex justify-center">
                     <Sparkline 
                       data={etf.sparkline} 
                       color={(etf.sparkline?.[etf.sparkline.length-1] || 0) >= (etf.sparkline?.[0] || 0) ? "#10b981" : "#f43f5e"} 
                     />
                   </div>
                </td>
                <td className="p-3 text-center">
                   <span className={`text-[11px] font-black uppercase px-2 py-0.5 rounded-full border ${
                     etf.trend_status.includes('Above All') ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 
                     etf.trend_status.includes('Below All') ? 'bg-rose-500/10 text-rose-500 border-rose-500/20' : 
                     'bg-zinc-800 text-zinc-500 border-zinc-700'
                   }`}>
                     {etf.trend_status.split(' ')[0]}
                   </span>
                </td>
              </tr>
              {expandedTicker === etf.ticker && (
                <tr>
                  <td colSpan="11" className="p-0 border-b border-zinc-800">
                    <ETFTradeCard etf={etf} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ETFTable;
